import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Search, Copy, Check, ArrowDownToLine, RotateCw, ArrowLeft } from "lucide-react";
import {
  Badge,
  Banner,
  BodyText,
  Button,
  Collapsible,
  FieldCheckboxButton,
  FieldText,
  Flex,
  Heading,
  LoadingAnimation,
  Modal,
  ScrollableArea,
  Stack,
} from "@knowtex-ai/components";
import { toast } from "react-toastify";
import useFetchRecentNote from "../../../hooks/useFetchRecentNote";
import { SUMMARY_PASTED_CONTENT_PROMPT } from "../../noteList/SmartAssistPromptTools";
import {
  groupNotesByTitle,
  MAX_CONCURRENT_NOTE_FETCHES,
  type DecodedNoteResult,
  type RetrievedNoteMetadata,
} from "../../../services/vaNotesService";
import styles from "./PreviousNoteModal.module.css";

export interface PreviousNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInsertNote: (notes: Array<{ text: string; metadata: RetrievedNoteMetadata }>) => void;
  patientName?: string;
  doctorName?: string;
  patientIcn: string;
  sta3n?: string;
  patientId?: number | null;
  isSmartAssistAvailable?: boolean;
  onSummarizeWithSmartAssist?: (prompt: string) => void;
}

type ModalView = "list" | "preview";

export const LAST_SIGNED_NOTE_LABEL = "Your Last Signed Note";
export const OTHER_NOTES_LABEL = "Other notes";
const SELECTION_HINT_ID = "signed-notes-selection-hint";

const STEP_LABELS = [
  { key: "connecting", label: "Connecting to VA & verifying credentials" },
  { key: "searching", label: "Searching patient clinical documents" },
  { key: "locating", label: "Locating your most recent signed note" },
  { key: "retrieving", label: "Retrieving note binary from VA" },
  { key: "decoding", label: "Decoding note" },
];

export function formatNoteDate(date: string): string {
  if (!date) return "Unknown date";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function formatNoteChoiceName(note: {
  title?: string;
  date?: string;
  authorDisplay?: string;
}): string {
  const parts = [note.title || "Clinical Note", formatNoteDate(note.date || "")];
  if (note.authorDisplay) {
    parts.push(note.authorDisplay);
  }
  return parts.join(", ");
}

export function formatLastSignedChoiceName(note: { title?: string; date?: string }): string {
  return `${LAST_SIGNED_NOTE_LABEL}, ${formatNoteChoiceName(note)}`;
}

export function toNoteGroupListId(title: string): string {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return `signed-note-group-${slug || "untitled"}`;
}

export function formatNotesForContext(noteResults: DecodedNoteResult[]): string {
  return noteResults
    .map((result) => {
      const dateLabel = result.metadata.date
        ? new Date(result.metadata.date).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          })
        : "";
      const header = dateLabel
        ? `--- Previous Clinical Note (${dateLabel}) ---`
        : "--- Previous Clinical Note ---";
      return `${header}\n\n${(result.text || "").trim()}`;
    })
    .join("\n\n");
}

export const PreviousNoteModal: React.FC<PreviousNoteModalProps> = ({
  isOpen,
  onClose,
  onInsertNote,
  patientName = "Patient",
  doctorName,
  patientIcn,
  sta3n,
  patientId,
  isSmartAssistAvailable = false,
  onSummarizeWithSmartAssist,
}) => {
  const resolvedDoctorName =
    doctorName ||
    (typeof localStorage !== "undefined" ? localStorage.getItem("userName") || "" : "");

  const {
    fetchNoteList,
    fetchNotesByMetadata,
    isLoading,
    step,
    stepMessage,
    error,
    documents,
    lastProviderNote,
    cachedNotes,
    noteResults,
    dismissError,
    reset,
  } = useFetchRecentNote({
    patientIcn,
    preferredSta3n: sta3n,
    patientName,
    doctorName: resolvedDoctorName,
    patientId,
  });

  const lastBinarySelectionRef = useRef<RetrievedNoteMetadata[] | null>(null);
  const hasAppliedCacheSelectionRef = useRef(false);

  const [view, setView] = useState<ModalView>("list");
  const [hasCopied, setHasCopied] = useState(false);
  const [titleQuery, setTitleQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isOpen) {
      setHasCopied(false);
      setView("list");
      setTitleQuery("");
      setSelectedIds(new Set());
      lastBinarySelectionRef.current = null;
      hasAppliedCacheSelectionRef.current = false;
      fetchNoteList();
    } else {
      reset();
    }
  }, [isOpen, fetchNoteList, reset]);

  useEffect(() => {
    if (!isOpen || hasAppliedCacheSelectionRef.current || !cachedNotes.length) {
      return;
    }
    if (!documents.length && !lastProviderNote) {
      return;
    }

    const allowedIds = new Set(documents.map((note) => note.id));
    if (lastProviderNote) {
      allowedIds.add(lastProviderNote.id);
    }

    const nextSelected = cachedNotes
      .map((note) => note.id)
      .filter((noteId) => allowedIds.has(noteId));
    if (nextSelected.length) {
      setSelectedIds(new Set(nextSelected));
    }
    hasAppliedCacheSelectionRef.current = true;
  }, [cachedNotes, documents, isOpen, lastProviderNote]);

  const titleQueryNormalized = titleQuery.trim().toLowerCase();
  const titleGroups = useMemo(() => {
    const listedDocuments = lastProviderNote
      ? documents.filter((note) => note.id !== lastProviderNote.id)
      : documents;
    const groups = groupNotesByTitle(listedDocuments);
    if (!titleQueryNormalized) return groups;
    return groups.filter((group) => group.title.toLowerCase().includes(titleQueryNormalized));
  }, [documents, lastProviderNote, titleQueryNormalized]);

  const showLastSignedNote = Boolean(lastProviderNote);

  const selectedNotes = useMemo(
    () => documents.filter((note) => selectedIds.has(note.id)),
    [documents, selectedIds]
  );

  const displayText = formatNotesForContext(noteResults) || "";
  const previewTitle =
    noteResults.length === 1
      ? noteResults[0].metadata.title
      : noteResults.length > 1
        ? `${noteResults.length} Clinical Notes`
        : "Previous Clinical Note";
  const modalTitle =
    view === "list" ? `Signed Notes — ${patientName}` : `${previewTitle} — ${patientName}`;

  const handleCopy = useCallback(async () => {
    if (!displayText) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(displayText);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = displayText;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }

      setHasCopied(true);
      toast.success("Note copied to clipboard");
      setTimeout(() => setHasCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy note:", err);
      toast.error("Failed to copy note to clipboard");
    }
  }, [displayText]);

  const handleInsert = useCallback(() => {
    const notes = noteResults
      .filter((result) => result.text)
      .map((result) => ({ text: result.text, metadata: result.metadata }));
    if (!notes.length) return;
    onInsertNote(notes);
    onClose();
  }, [noteResults, onInsertNote, onClose]);

  const handleLoadSelected = useCallback(async () => {
    if (!selectedNotes.length) return;
    lastBinarySelectionRef.current = selectedNotes;
    const results = await fetchNotesByMetadata(selectedNotes);
    if (results?.length) {
      setView("preview");
    }
  }, [selectedNotes, fetchNotesByMetadata]);

  const handleRetry = useCallback(() => {
    if (lastBinarySelectionRef.current?.length && documents.length) {
      void fetchNotesByMetadata(lastBinarySelectionRef.current).then((results) => {
        if (results?.length) {
          setView("preview");
        }
      });
      return;
    }
    void fetchNoteList();
  }, [documents.length, fetchNoteList, fetchNotesByMetadata]);

  const handleDismissError = useCallback(() => {
    dismissError();
    setView("list");
  }, [dismissError]);

  const handleNoteCheckedChange = useCallback((noteId: string, checked: boolean) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (!checked) {
        next.delete(noteId);
        return next;
      }
      if (next.has(noteId)) {
        return current;
      }
      if (next.size >= MAX_CONCURRENT_NOTE_FETCHES) {
        toast.info(`You can select up to ${MAX_CONCURRENT_NOTE_FETCHES} notes.`);
        return current;
      }
      next.add(noteId);
      return next;
    });
  }, []);

  const handleSummarize = useCallback(() => {
    if (!onSummarizeWithSmartAssist || !noteResults.length) {
      toast.error("Smart Assist is not available for these notes.");
      return;
    }

    onSummarizeWithSmartAssist(
      `${SUMMARY_PASTED_CONTENT_PROMPT}${formatNotesForContext(noteResults)}`
    );
    onClose();
  }, [noteResults, onClose, onSummarizeWithSmartAssist]);

  const handleBack = useCallback(() => {
    setHasCopied(false);
    setView("list");
  }, []);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        onClose();
      }
    },
    [onClose]
  );

  if (!isOpen) return null;

  const currentStepIndex = STEP_LABELS.findIndex((s) => s.key === step);
  const canSummarize = Boolean(
    isSmartAssistAvailable && onSummarizeWithSmartAssist && noteResults.length
  );
  const selectionAtCap = selectedIds.size >= MAX_CONCURRENT_NOTE_FETCHES;

  return (
    <Modal
      open={isOpen}
      onOpenChange={handleOpenChange}
      title={modalTitle}
      size="large"
      className={styles.modal}
    >
      <Stack gap={16} className={styles.body}>
        {view === "preview" && noteResults[0]?.metadata && (
          <Flex wrap="wrap" alignItems="center" gap={8}>
            {noteResults.length === 1 && noteResults[0].metadata.date && (
              <Badge variant="info" size="small">
                Date: {formatNoteDate(noteResults[0].metadata.date)}
              </Badge>
            )}
            {noteResults.length > 1 && (
              <Badge variant="info" size="small">
                {noteResults.length} notes selected
              </Badge>
            )}
            <Badge variant="success" size="small">
              {noteResults[0].metadata.docStatus || "final"}
            </Badge>
            {noteResults[0].metadata.authorDisplay &&
              noteResults[0].metadata.id !== lastProviderNote?.id && (
                <Badge size="small">Author: {noteResults[0].metadata.authorDisplay}</Badge>
              )}
            {sta3n && (
              <Badge variant="warning" size="small">
                Site: {sta3n}
              </Badge>
            )}
          </Flex>
        )}

        {isLoading && (
          <div
            className={styles.loadingContainer}
            id="signed-notes-loading-status"
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <LoadingAnimation size={48} color="#783BFF" />
            <Heading as="h3" size="x-small">
              {stepMessage || "Loading note..."}
            </Heading>
            <BodyText muted>Retrieving signed clinical notes from VA.</BodyText>

            <ol className={styles.loadingProgressSteps}>
              {STEP_LABELS.map((item, idx) => {
                const isDone = currentStepIndex > idx || step === "complete";
                const isActive = currentStepIndex === idx;

                return (
                  <li
                    key={item.key}
                    className={`${styles.progressStepItem} ${
                      isActive ? styles.progressStepActive : ""
                    } ${isDone ? styles.progressStepDone : ""}`}
                    aria-current={isActive ? "step" : undefined}
                  >
                    <span
                      className={`${styles.stepDot} ${
                        isActive ? styles.stepDotActive : ""
                      } ${isDone ? styles.stepDotDone : ""}`}
                      aria-hidden="true"
                    />
                    <span>{item.label}</span>
                  </li>
                );
              })}
            </ol>
          </div>
        )}

        {!isLoading && error && (
          <Banner
            variant="error"
            showIcon
            heading="Unable to Retrieve Clinical Note"
            actions={
              <Flex gap={12} wrap="wrap">
                <Button variant="primary-alt" icon={RotateCw} onClick={handleRetry}>
                  Retry
                </Button>
                {documents.length > 0 && (
                  <Button variant="secondary" icon={ArrowLeft} onClick={handleDismissError}>
                    Back
                  </Button>
                )}
                <Button variant="secondary" onClick={onClose}>
                  Close
                </Button>
              </Flex>
            }
          >
            <span id="signed-notes-error-message">{error}</span>
          </Banner>
        )}

        {!isLoading && !error && view === "list" && (
          <div className={styles.listContainer}>
            <FieldText
              label="Search note titles"
              value={titleQuery}
              onChange={(event) => setTitleQuery(event.target.value)}
              placeholder="Search note titles"
              icon={Search}
            />

            <BodyText size="small" muted as="p" className={styles.selectionHint}>
              <span id={SELECTION_HINT_ID} role="status" aria-live="polite">
                Select up to {MAX_CONCURRENT_NOTE_FETCHES} notes. Your last signed note is listed
                first when available.
                {selectionAtCap ? " Maximum of 5 notes selected." : ""}
              </span>
            </BodyText>

            <div className={styles.listBody}>
              {showLastSignedNote && lastProviderNote && (
                <>
                  <FieldCheckboxButton
                    className={styles.lastSignedChoice}
                    checked={selectedIds.has(lastProviderNote.id)}
                    disabled={!selectedIds.has(lastProviderNote.id) && selectionAtCap}
                    onCheckedChange={(checked) =>
                      handleNoteCheckedChange(lastProviderNote.id, checked)
                    }
                  >
                    <span className={styles.lastSignedCopy}>
                      <span className={styles.lastSignedLabel}>{LAST_SIGNED_NOTE_LABEL}</span>
                      <span className={styles.lastSignedMeta}>
                        {lastProviderNote.title}
                        {lastProviderNote.date ? ` · ${formatNoteDate(lastProviderNote.date)}` : ""}
                      </span>
                    </span>
                  </FieldCheckboxButton>
                  <div className={styles.lastSignedDivider} role="separator">
                    <span className={styles.lastSignedDividerLabel}>{OTHER_NOTES_LABEL}</span>
                  </div>
                </>
              )}

              <ScrollableArea fill className={styles.titleGroups}>
                {titleGroups.length === 0 && (
                  <BodyText muted as="p" className={styles.emptySearch}>
                    <span role="status">No note titles match your search.</span>
                  </BodyText>
                )}
                {titleGroups.length > 0 && (
                  <Stack gap={8}>
                    {titleGroups.map((group) => {
                      const selectedInGroup = group.notes.filter((note) =>
                        selectedIds.has(note.id)
                      ).length;
                      const showSelectedCount = selectedInGroup > 0;
                      return (
                        <Collapsible
                          key={group.title}
                          className={styles.titleGroup}
                          trigger={
                            <span className={styles.titleGroupTrigger}>
                              <span>{group.title}</span>
                              <Badge size="small" variant={showSelectedCount ? "info" : "default"}>
                                {showSelectedCount
                                  ? `Selected ${selectedInGroup}/${group.notes.length}`
                                  : group.notes.length}
                              </Badge>
                            </span>
                          }
                        >
                          <Stack gap={8}>
                            {group.notes.map((note) => {
                              const isSelected = selectedIds.has(note.id);
                              const isDisabled = !isSelected && selectionAtCap;
                              return (
                                <FieldCheckboxButton
                                  key={note.id}
                                  className={styles.noteChoice}
                                  checked={isSelected}
                                  disabled={isDisabled}
                                  onCheckedChange={(checked) =>
                                    handleNoteCheckedChange(note.id, checked)
                                  }
                                >
                                  <span className={styles.noteChoiceCopy}>
                                    <span className={styles.srOnly}>{note.title}, </span>
                                    <span className={styles.noteInstanceDate}>
                                      {formatNoteDate(note.date)}
                                    </span>
                                    {note.authorDisplay && (
                                      <span className={styles.noteInstanceAuthor}>
                                        {note.authorDisplay}
                                      </span>
                                    )}
                                  </span>
                                </FieldCheckboxButton>
                              );
                            })}
                          </Stack>
                        </Collapsible>
                      );
                    })}
                  </Stack>
                )}
              </ScrollableArea>
            </div>
          </div>
        )}

        {!isLoading && !error && view === "preview" && noteResults.length > 0 && (
          <ScrollableArea fill className={styles.contentViewer}>
            <pre className={styles.noteText}>{displayText || "No text content found in note."}</pre>
          </ScrollableArea>
        )}

        <Flex justifyContent="space-between" alignItems="center" gap={12} wrap="wrap">
          <Flex alignItems="center" gap={12} wrap="wrap">
            {!isLoading && !error && view === "preview" && (
              <Button variant="secondary" icon={ArrowLeft} onClick={handleBack}>
                Back
              </Button>
            )}
            {!isLoading && view === "preview" && noteResults.length > 0 && (
              <Button variant="secondary" icon={hasCopied ? Check : Copy} onClick={handleCopy}>
                {hasCopied ? "Copied" : "Copy Text"}
              </Button>
            )}
          </Flex>

          <Flex alignItems="center" gap={12} wrap="wrap">
            <Button variant="text" onClick={onClose}>
              Cancel
            </Button>
            {!isLoading && !error && view === "list" && (
              <Button
                variant="primary-alt"
                onClick={() => void handleLoadSelected()}
                disabled={selectedNotes.length === 0}
                aria-describedby={selectedNotes.length === 0 ? SELECTION_HINT_ID : undefined}
              >
                {selectedNotes.length ? `Load ${selectedNotes.length} selected` : "Load selected"}
              </Button>
            )}
            {!isLoading && view === "preview" && noteResults.length > 0 && canSummarize && (
              <Button variant="secondary" onClick={handleSummarize}>
                Summarize with Smart Assist
              </Button>
            )}
            {!isLoading && view === "preview" && noteResults.length > 0 && (
              <Button variant="primary-alt" icon={ArrowDownToLine} onClick={handleInsert}>
                Insert into Additional EHR Context
              </Button>
            )}
          </Flex>
        </Flex>
      </Stack>
    </Modal>
  );
};

export default PreviousNoteModal;
